import React, { useState, useEffect } from 'react';
import { useProjects } from '../../lib/ProjectContext';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Feature } from '../../lib/types';

interface SaveProjectProps {
  selectedFeatures: Feature[];
  componentCount: number;
  onLoginRequired: () => void;
}

const SaveProject: React.FC<SaveProjectProps> = ({ 
  selectedFeatures, 
  componentCount,
  onLoginRequired 
}) => {
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { saveProject, currentProject, updateProject } = useProjects();
  const { currentUser } = useAuth();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      onLoginRequired();
      return;
    }

    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (currentProject?.id) {
        await updateProject(currentProject.id, {
          name: projectName,
          description: projectDescription,
          selectedFeatures,
          componentCount
        });
        setSuccess('Project updated successfully');
      } else {
        await saveProject(
          projectName,
          projectDescription,
          selectedFeatures,
          componentCount
        );
        setSuccess('Project saved successfully');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving the project');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    // Pre-fill form if editing an existing project
    if (currentProject) {
      setProjectName(currentProject.name);
      setProjectDescription(currentProject.description || '');
    } else {
      setProjectName('');
      setProjectDescription('');
    }
  }, [currentProject]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {currentProject?.id ? 'Update Project' : 'Save Project'}
        </CardTitle>
        <CardDescription>
          {currentProject?.id 
            ? 'Update your project settings' 
            : 'Save your project for future reference'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        <form onSubmit={handleSave}>
          <div className="space-y-4">
            <div>
              <label htmlFor="projectName" className="block text-sm font-medium mb-1">
                Project Name
              </label>
              <input
                type="text"
                id="projectName"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full p-2 border rounded-md"
                disabled={saving}
                required
              />
            </div>
            <div>
              <label htmlFor="projectDescription" className="block text-sm font-medium mb-1">
                Project Description (optional)
              </label>
              <textarea
                id="projectDescription"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                className="w-full p-2 border rounded-md"
                rows={3}
                disabled={saving}
              />
            </div>
            <div>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : currentProject?.id ? 'Update Project' : 'Save Project'}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
      {!currentUser && (
        <CardFooter>
          <p className="text-sm text-center w-full text-muted-foreground">
            You need to be logged in to save projects.{' '}
            <Button variant="link" className="p-0" onClick={onLoginRequired}>
              Login or Sign Up
            </Button>
          </p>
        </CardFooter>
      )}
    </Card>
  );
};

export default SaveProject; 